import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { isAdmin, isViewer, pendingInvitesCount } from "@/lib/guards";
import { usePwaContext } from "@/contexts/PwaContext";
import PwaProfileSection from "@/components/pwa/PwaProfileSection";
import { friendlyError } from "@/lib/errors";
import AppHeader from "@/components/AppHeader";
import AccountSwitcher from "@/components/account/AccountSwitcher";
import AuthField from "@/components/shared/AuthField";
import { ListSkeleton, ErrorState } from "@/components/shared/StateViews";
import {
  COLLAB_LIMIT,
  listCollaborators,
  inviteCollaborator,
  resendInvite,
  revokeCollaborator,
  listInvites,
  acceptInvite,
  declineInvite,
  liveGrantCount,
  canInvite,
  isValidEmail,
  statusChip,
  formatDatePtBr,
} from "@/lib/collaborators";
import { PROFILE, COLLAB, INVITES } from "@/constants/testIds/profile";
import {
  UserRound, Pencil, Check, X, ShieldCheck, LogOut, Loader2,
  Users, Mail, RotateCcw, Trash2, Sparkles, ArrowLeftRight,
} from "lucide-react";
import { toast } from "sonner";

const PLAN_LABELS = { free: "Gratuito", pro: "Pro", premium: "Premium" };

// ── Profile / account page (Epic 10.4) ──────────────────────────────────────
export default function Profile() {
  const { user, refresh, logout } = useAuth();
  const nav = useNavigate();
  const viewer = isViewer(user);

  return (
    <div className="min-h-screen bg-brand-bg starfield relative" data-testid={PROFILE.page}>
      <AppHeader />
      <div className="max-w-3xl mx-auto px-5 sm:px-6 py-8 lg:py-10 pb-28 md:pb-12">
        <header className="mb-7">
          <div className="text-[11px] tracking-[0.24em] uppercase text-brand-rose font-ui mb-2">
            Minha conta
          </div>
          <h1 className="font-display text-4xl sm:text-5xl text-brand-ink">
            {user?.name?.split(" ")[0] || "Perfil"} ✦
          </h1>
        </header>

        <div className="space-y-5">
          <SwitcherSection />
          <ReceivedInvitesSection />
          <MyDataSection user={user} onSaved={refresh} />
          <SecuritySection />
          <PlanSection user={user} />
          {!viewer && <PersonaResetSection onReset={refresh} navigate={nav} />}
          <PwaAccountBlock user={user} />
          <CollaboratorsSection viewer={viewer} />
          <SessionsSection onLoggedOut={() => { logout(); nav("/"); }} />
          {isAdmin(user) && (
            <Link
              to="/admin"
              data-testid={PROFILE.adminLink}
              className="flex items-center justify-center gap-2 w-full min-h-[44px] rounded-2xl border border-brand-line bg-brand-surface text-brand-ink hover:text-brand-rose hover:border-brand-rose transition-colors text-[14px] font-ui"
            >
              <ShieldCheck className="w-4 h-4" /> Abrir painel admin
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Reusable section shell ──────────────────────────────────────────────────
function Section({ icon: Icon, title, description, children, testid, action }) {
  return (
    <section
      data-testid={testid}
      className="bg-brand-surface border border-brand-line rounded-3xl p-5 sm:p-6"
    >
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="flex items-start gap-3 min-w-0">
          {Icon && (
            <span className="shrink-0 w-9 h-9 rounded-xl bg-brand-elevated text-brand-rose flex items-center justify-center">
              <Icon className="w-4 h-4" />
            </span>
          )}
          <div className="min-w-0">
            <h2 className="font-display text-xl text-brand-ink leading-tight">{title}</h2>
            {description && (
              <p className="text-[12.5px] text-brand-ink2 font-ui mt-0.5">{description}</p>
            )}
          </div>
        </div>
        {action}
      </div>
      {children}
    </section>
  );
}

// ── Switcher ────────────────────────────────────────────────────────────────
function SwitcherSection() {
  return (
    <Section icon={ArrowLeftRight} title="Trocar de conta" testid="profile-switcher-section">
      <AccountSwitcher title="" />
    </Section>
  );
}

// ── Meus dados ──────────────────────────────────────────────────────────────
function MyDataSection({ user, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(user?.name || "");
  const [saving, setSaving] = useState(false);

  useEffect(() => { setName(user?.name || ""); }, [user?.name]);

  const save = async () => {
    const trimmed = name.trim();
    if (!trimmed) { toast.error("O nome não pode ficar vazio."); return; }
    if (trimmed === user?.name) { setEditing(false); return; }
    setSaving(true);
    try {
      await api.patch("/auth/me", { name: trimmed });
      await onSaved?.();
      setEditing(false);
      toast.success("Nome atualizado ✦");
    } catch (err) {
      toast.error(friendlyError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Section icon={UserRound} title="Meus dados" testid="profile-mydata-section">
      <div className="space-y-4">
        <div>
          <span className="block text-[11px] tracking-[0.22em] uppercase text-brand-ink2 font-ui mb-2">
            Nome
          </span>
          {editing ? (
            <div className="flex items-center gap-2">
              <input
                data-testid={PROFILE.nameInput}
                value={name}
                onChange={(e) => setName(e.target.value)}
                autoFocus
                onKeyDown={(e) => { if (e.key === "Enter") save(); if (e.key === "Escape") { setName(user?.name || ""); setEditing(false); } }}
                className="flex-1 px-4 py-3 rounded-xl border border-brand-line bg-brand-elevated text-white focus:border-brand-rose focus:outline-none text-[16px]"
              />
              <button
                type="button"
                data-testid={PROFILE.nameSave}
                onClick={save}
                disabled={saving}
                aria-label="Salvar nome"
                className="shrink-0 w-11 h-11 flex items-center justify-center rounded-xl bg-brand-rose text-white hover:bg-brand-roseHover transition-colors disabled:opacity-50"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
              </button>
              <button
                type="button"
                data-testid={PROFILE.nameCancel}
                onClick={() => { setName(user?.name || ""); setEditing(false); }}
                aria-label="Cancelar"
                className="shrink-0 w-11 h-11 flex items-center justify-center rounded-xl bg-brand-elevated border border-brand-line text-brand-ink2 hover:text-brand-rose transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <div className="flex items-center justify-between gap-2">
              <span className="text-[15px] text-brand-ink font-ui truncate">{user?.name || "—"}</span>
              <button
                type="button"
                data-testid={PROFILE.nameEdit}
                onClick={() => setEditing(true)}
                className="shrink-0 inline-flex items-center gap-1.5 px-3 py-2 rounded-full bg-brand-elevated border border-brand-line text-[12.5px] text-brand-ink2 hover:text-brand-rose hover:border-brand-rose transition-colors min-h-[40px]"
              >
                <Pencil className="w-3.5 h-3.5" /> Editar
              </button>
            </div>
          )}
        </div>

        <div>
          <span className="block text-[11px] tracking-[0.22em] uppercase text-brand-ink2 font-ui mb-2">
            E-mail
          </span>
          <div className="flex items-center justify-between gap-2">
            <span data-testid={PROFILE.emailValue} className="text-[15px] text-brand-ink2 font-ui truncate">
              {user?.email || "—"}
            </span>
            <span className="shrink-0 text-[11px] text-brand-ink3 font-ui">não editável</span>
          </div>
        </div>
      </div>
    </Section>
  );
}

// ── Segurança (trocar senha) ────────────────────────────────────────────────
function SecuritySection() {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!current) { toast.error("Digite sua senha atual."); return; }
    if (next.length < 6) { toast.error("A nova senha precisa ter pelo menos 6 caracteres."); return; }
    if (next !== confirm) { toast.error("A confirmação não corresponde à nova senha."); return; }
    if (next === current) { toast.error("A nova senha precisa ser diferente da atual."); return; }
    setSaving(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      setCurrent(""); setNext(""); setConfirm("");
      toast.success("Senha alterada — outras sessões foram desconectadas ✦");
    } catch (err) {
      toast.error(friendlyError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Section icon={ShieldCheck} title="Segurança" description="Trocar sua senha desconecta os outros dispositivos." testid="profile-security-section">
      <form onSubmit={submit} className="space-y-4">
        <AuthField
          label="Senha atual" type="password" value={current} onChange={setCurrent}
          testid={PROFILE.pwCurrent} name="current-password" autoComplete="current-password"
        />
        <AuthField
          label="Nova senha" type="password" value={next} onChange={setNext}
          testid={PROFILE.pwNew} name="new-password" autoComplete="new-password"
        />
        <AuthField
          label="Confirmar nova senha" type="password" value={confirm} onChange={setConfirm}
          testid={PROFILE.pwConfirm} name="confirm-password" autoComplete="new-password"
        />
        <button
          type="submit"
          data-testid={PROFILE.pwSubmit}
          disabled={saving}
          className="w-full min-h-[44px] rounded-full bg-brand-rose hover:bg-brand-roseHover text-white text-[14px] font-ui transition-colors disabled:opacity-50 inline-flex items-center justify-center gap-2"
        >
          {saving && <Loader2 className="w-4 h-4 animate-spin" />} Atualizar senha
        </button>
      </form>
    </Section>
  );
}

// ── Plano ───────────────────────────────────────────────────────────────────
function PwaAccountBlock({ user }) {
  const pwa = usePwaContext();
  if (!pwa) return null;
  return (
    <PwaProfileSection
      user={user}
      standalone={pwa.standalone}
      ios={pwa.ios}
      config={pwa.config}
      prefs={pwa.prefs}
      onInstall={pwa.onInstall}
      onPatchPrefs={pwa.patchPrefs}
      onTestPush={pwa.sendTestPush}
    />
  );
}

function PlanSection({ user }) {
  const label = PLAN_LABELS[user?.plan] || "Gratuito";
  return (
    <Section icon={Sparkles} title="Plano" testid="profile-plan-section">
      <div className="flex items-center justify-between gap-3">
        <span
          data-testid={PROFILE.planBadge}
          className="px-3 py-1 rounded-full bg-brand-elevated text-brand-rose text-[13px] font-ui"
        >
          {label}
        </span>
        <span className="text-[12.5px] text-brand-ink2 font-ui text-right">
          Para mudar de plano, fale com quem administra a sua conta.
        </span>
      </div>
    </Section>
  );
}

// ── Brand Persona: refazer do zero (duplo aceite) ───────────────────────────
export const PERSONA_RESET_WORD = "RESETAR";

export function PersonaResetSection({ onReset, navigate }) {
  const [step, setStep] = useState(0); // 0 fechado · 1 explicação · 2 digitar RESETAR
  const [word, setWord] = useState("");
  const [busy, setBusy] = useState(false);
  const typed = word.trim().toUpperCase();
  const canConfirm = typed === PERSONA_RESET_WORD && !busy;

  const cancel = () => { setStep(0); setWord(""); };

  const run = async () => {
    if (!canConfirm) return;
    setBusy(true);
    try {
      await api.post("/persona/me/reset", { confirm: typed });
    } catch (err) {
      toast.error(friendlyError(err));
      setBusy(false);
      return;
    }
    toast.success("Brand Persona apagada. Vamos recomeçar do zero ✦");
    // A persona já foi apagada: mesmo que o refresh falhe, seguimos para o
    // onboarding — a guarda de rota recompõe o estado no destino.
    try { await onReset?.(); } catch { /* guard de rota resolve no destino */ }
    setBusy(false);
    navigate?.("/onboarding/brand-persona?reset=1", { replace: true });
  };

  return (
    <Section
      icon={RotateCcw}
      title="Brand Persona"
      description="Mudou de nicho ou de posicionamento? Refaça a entrevista do zero."
      testid={PROFILE.personaResetSection}
    >
      {step === 0 && (
        <button
          type="button"
          data-testid={PROFILE.personaResetTrigger}
          onClick={() => setStep(1)}
          className="w-full min-h-[44px] rounded-full border border-brand-line bg-brand-elevated text-brand-ink hover:text-brand-rose hover:border-brand-rose text-[13.5px] font-ui transition-colors inline-flex items-center justify-center gap-2"
        >
          <RotateCcw className="w-4 h-4" /> Refazer minha Brand Persona
        </button>
      )}

      {step === 1 && (
        <div className="space-y-4">
          <div className="rounded-2xl border border-brand-line bg-brand-elevated p-4 text-[13.5px] font-ui space-y-3">
            <p className="text-brand-ink">Ao refazer, você volta para a entrevista inicial como no primeiro acesso.</p>
            <div>
              <div className="text-[11px] tracking-[0.18em] uppercase text-brand-rose mb-1">Será apagado</div>
              <ul className="list-disc pl-5 text-brand-ink2 space-y-0.5">
                <li>Sua Brand Persona atual (todos os campos da entrevista que os agentes usam hoje).</li>
              </ul>
            </div>
            <div>
              <div className="text-[11px] tracking-[0.18em] uppercase text-brand-sage mb-1">Fica guardado</div>
              <ul className="list-disc pl-5 text-brand-ink2 space-y-0.5">
                <li>Avatares, conteúdos da biblioteca (inclusive o Manual da marca já salvo) e calendário.</li>
                <li>Conversas com os agentes, inclusive a entrevista anterior (marcada como anterior).</li>
              </ul>
            </div>
            <p className="text-brand-ink2">Painel e agentes ficam fechados até você confirmar a nova persona e o avatar.</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              data-testid={PROFILE.personaResetContinue}
              onClick={() => setStep(2)}
              className="flex-1 min-h-[44px] rounded-full bg-brand-rose hover:bg-brand-roseHover text-white text-[13.5px] font-ui transition-colors"
            >
              Entendi, quero continuar
            </button>
            <button
              type="button"
              data-testid={PROFILE.personaResetCancel}
              onClick={cancel}
              className="min-h-[44px] px-4 rounded-full bg-brand-elevated border border-brand-line text-brand-ink2 hover:text-brand-rose text-[13.5px] font-ui transition-colors"
            >
              Cancelar
            </button>
          </div>
        </div>
      )}

      {step === 2 && (
        <div className="space-y-3">
          <label htmlFor="persona-reset-word" className="block text-[12.5px] text-brand-ink2 font-ui">
            Para confirmar, digite <span className="text-brand-ink font-semibold">{PERSONA_RESET_WORD}</span>:
          </label>
          <input
            id="persona-reset-word"
            data-testid={PROFILE.personaResetInput}
            value={word}
            onChange={(e) => setWord(e.target.value)}
            autoFocus
            autoComplete="off"
            placeholder={PERSONA_RESET_WORD}
            onKeyDown={(e) => { if (e.key === "Enter") run(); if (e.key === "Escape") cancel(); }}
            className="w-full px-4 py-3 rounded-xl border border-brand-line bg-brand-elevated text-white focus:border-brand-rose focus:outline-none text-[16px] tracking-[0.2em] uppercase"
          />
          <div className="flex items-center gap-2">
            <button
              type="button"
              data-testid={PROFILE.personaResetConfirm}
              onClick={run}
              disabled={!canConfirm}
              className="flex-1 min-h-[44px] rounded-full bg-brand-rose hover:bg-brand-roseHover text-white text-[13.5px] font-ui transition-colors disabled:opacity-50 inline-flex items-center justify-center gap-2"
            >
              {busy && <Loader2 className="w-4 h-4 animate-spin" />} Apagar e recomeçar
            </button>
            <button
              type="button"
              data-testid={PROFILE.personaResetCancel}
              onClick={cancel}
              disabled={busy}
              className="min-h-[44px] px-4 rounded-full bg-brand-elevated border border-brand-line text-brand-ink2 hover:text-brand-rose text-[13.5px] font-ui transition-colors"
            >
              Cancelar
            </button>
          </div>
        </div>
      )}
    </Section>
  );
}

// ── Sessões ─────────────────────────────────────────────────────────────────
function SessionsSection({ onLoggedOut }) {
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(false);

  const logoutAll = async () => {
    setBusy(true);
    try {
      await api.post("/auth/logout-all").catch(() => {});
      toast.success("Você saiu de todos os dispositivos.");
    } finally {
      setBusy(false);
      onLoggedOut?.();
    }
  };

  return (
    <Section icon={LogOut} title="Sessões" description="Encerra o acesso em todos os aparelhos conectados." testid="profile-sessions-section">
      {confirming ? (
        <div className="flex items-center gap-2">
          <button
            type="button"
            data-testid={PROFILE.logoutAll}
            onClick={logoutAll}
            disabled={busy}
            className="flex-1 min-h-[44px] rounded-full bg-brand-rose hover:bg-brand-roseHover text-white text-[13.5px] font-ui transition-colors disabled:opacity-50 inline-flex items-center justify-center gap-2"
          >
            {busy && <Loader2 className="w-4 h-4 animate-spin" />} Confirmar e sair de tudo
          </button>
          <button
            type="button"
            onClick={() => setConfirming(false)}
            className="min-h-[44px] px-4 rounded-full bg-brand-elevated border border-brand-line text-brand-ink2 hover:text-brand-rose text-[13.5px] font-ui transition-colors"
          >
            Cancelar
          </button>
        </div>
      ) : (
        <button
          type="button"
          data-testid="profile-logout-all-trigger"
          onClick={() => setConfirming(true)}
          className="w-full min-h-[44px] rounded-full border border-brand-line bg-brand-elevated text-brand-ink hover:text-brand-rose hover:border-brand-rose text-[13.5px] font-ui transition-colors inline-flex items-center justify-center gap-2"
        >
          <LogOut className="w-4 h-4" /> Sair de todos os dispositivos
        </button>
      )}
    </Section>
  );
}

// ── Convites recebidos ──────────────────────────────────────────────────────
function ReceivedInvitesSection() {
  const { user, refresh } = useAuth();
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [invitesError, setInvitesError] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setInvitesError(null);
    try {
      const { data } = await listInvites();
      setInvites(data?.invites || (Array.isArray(data) ? data : []));
    } catch (err) {
      setInvites([]);
      setInvitesError(friendlyError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load, user?.pending_invites_count]);

  const act = async (inv, action) => {
    setBusyId(inv.id);
    try {
      if (action === "accept") {
        await acceptInvite(inv.id);
        toast.success("Convite aceito — troque de conta no menu Conta ✦");
      } else {
        await declineInvite(inv.id);
        toast.success("Convite recusado.");
      }
      setInvites((list) => list.filter((i) => i.id !== inv.id));
      await refresh?.();
    } catch (err) {
      toast.error(friendlyError(err));
    } finally {
      setBusyId(null);
    }
  };

  // Nothing pending and none loaded — hide the section entirely to keep the page
  // lean. A load error keeps the section visible so the failure is explained.
  if (!loading && !invitesError && invites.length === 0 && pendingInvitesCount(user) === 0) return null;

  return (
    <Section icon={Mail} title="Convites recebidos" testid={INVITES.section}>
      {loading ? (
        <ListSkeleton count={1} />
      ) : invitesError ? (
        <ErrorState message={invitesError} onRetry={load} testid="invites-error" />
      ) : (
        <div className="space-y-2.5">
          {invites.map((inv) => (
            <div
              key={inv.id}
              data-testid={`${INVITES.item}-${inv.id}`}
              className="flex items-center gap-3 rounded-2xl bg-brand-elevated border border-brand-line p-3"
            >
              <span className="min-w-0 flex-1">
                <span className="block text-[14px] text-brand-ink font-ui truncate">
                  {inv.marca || inv.owner_name || "Nova marca"}
                </span>
                <span className="block text-[11.5px] text-brand-ink3 font-ui truncate">
                  Convite de {inv.owner_email || inv.owner_name || "um anfitrião"} · {formatDatePtBr(inv.invited_at)}
                </span>
              </span>
              <button
                type="button"
                data-testid={`${INVITES.accept}-${inv.id}`}
                onClick={() => act(inv, "accept")}
                disabled={busyId === inv.id}
                className="shrink-0 min-h-[40px] px-3.5 rounded-full bg-brand-rose text-white text-[12.5px] font-ui hover:bg-brand-roseHover transition-colors disabled:opacity-50"
              >
                {busyId === inv.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Aceitar"}
              </button>
              <button
                type="button"
                data-testid={`${INVITES.decline}-${inv.id}`}
                onClick={() => act(inv, "decline")}
                disabled={busyId === inv.id}
                className="shrink-0 min-h-[40px] px-3 rounded-full bg-brand-surface border border-brand-line text-brand-ink2 text-[12.5px] font-ui hover:text-brand-rose transition-colors disabled:opacity-50"
              >
                Recusar
              </button>
            </div>
          ))}
        </div>
      )}
    </Section>
  );
}

// ── Colaboradores (owner) ───────────────────────────────────────────────────
function CollaboratorsSection({ viewer }) {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [email, setEmail] = useState("");
  const [inviting, setInviting] = useState(false);
  const [feedback, setFeedback] = useState(null); // {tone, text}
  const [confirm, setConfirm] = useState(null); // {id, action, stage}

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await listCollaborators();
      setList(data?.collaborators || (Array.isArray(data) ? data : []));
    } catch (err) {
      // Endpoint not live yet → treat as empty rather than a hard error.
      if (err?.response?.status === 404) setList([]);
      else setError(friendlyError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const count = liveGrantCount(list);
  const roomLeft = canInvite(list);

  const invite = async (e) => {
    e.preventDefault();
    setFeedback(null);
    const value = email.trim();
    if (!isValidEmail(value)) { setFeedback({ tone: "err", text: "Digite um e-mail válido." }); return; }
    if (!roomLeft) { setFeedback({ tone: "err", text: `Limite de ${COLLAB_LIMIT} colaboradores atingido.` }); return; }
    setInviting(true);
    try {
      const { data } = await inviteCollaborator(value);
      setEmail("");
      const isNew = data?.new_user ?? data?.invited_new_user;
      setFeedback({
        tone: "ok",
        text: isNew === true
          ? "Convite enviado — a pessoa recebe um link de cadastro."
          : isNew === false
            ? "Convite enviado — a pessoa foi avisada no app."
            : "Convite enviado ✦",
      });
      toast.success("Convite enviado ✦");
      await load();
    } catch (err) {
      // friendlyError já resolve os códigos estruturados do backend
      // (collaborator_limit / already_invited / self_invite) para PT-BR — não
      // dá para presumir "limite atingido" só pelo status 409, que também é
      // devolvido em already_invited.
      setFeedback({ tone: "err", text: friendlyError(err) });
    } finally {
      setInviting(false);
    }
  };

  const doResend = async (id) => {
    setConfirm(null);
    try {
      await resendInvite(id);
      toast.success("Convite reenviado ✦");
    } catch (err) {
      toast.error(friendlyError(err));
    }
  };

  const doRevoke = async (id) => {
    setConfirm(null);
    try {
      await revokeCollaborator(id);
      setList((l) => l.filter((c) => c.id !== id));
      toast.success("Acesso removido.");
    } catch (err) {
      toast.error(friendlyError(err));
    }
  };

  const counter = (
    <span
      data-testid={COLLAB.counter}
      className={`shrink-0 text-[12px] font-ui px-2.5 py-1 rounded-full ${
        roomLeft ? "bg-brand-elevated text-brand-ink2" : "bg-brand-rose/15 text-brand-rose"
      }`}
    >
      {count}/{COLLAB_LIMIT}
    </span>
  );

  return (
    <Section
      icon={Users}
      title="Colaboradores"
      description="Convide até 3 pessoas para ver a sua marca (somente leitura)."
      testid={COLLAB.section}
      action={counter}
    >
      {viewer && (
        <p className="mb-3 text-[12px] text-brand-ink3 font-ui">
          Você está vendo outra conta. Isto gerencia os colaboradores da sua própria marca.
        </p>
      )}

      {/* Invite form: 1 field + 1 button */}
      <form onSubmit={invite} className="flex items-center gap-2 mb-2">
        <input
          data-testid={COLLAB.inviteInput}
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          disabled={!roomLeft || inviting}
          placeholder="email@dapessoa.com"
          className="flex-1 min-w-0 px-4 py-3 rounded-xl border border-brand-line bg-brand-elevated text-white focus:border-brand-rose focus:outline-none text-[16px] placeholder:text-brand-ink3 disabled:opacity-50"
        />
        <button
          type="submit"
          data-testid={COLLAB.inviteSubmit}
          disabled={!roomLeft || inviting}
          className="shrink-0 min-h-[44px] px-5 rounded-xl bg-brand-rose text-white text-[14px] font-ui hover:bg-brand-roseHover transition-colors disabled:opacity-50 inline-flex items-center gap-2"
        >
          {inviting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />} Convidar
        </button>
      </form>

      {feedback && (
        <p
          data-testid={COLLAB.inviteFeedback}
          className={`text-[12.5px] font-ui mb-3 ${feedback.tone === "ok" ? "text-brand-sage" : "text-brand-rose"}`}
        >
          {feedback.text}
        </p>
      )}
      {!roomLeft && !feedback && (
        <p data-testid={COLLAB.full} className="text-[12.5px] text-brand-rose font-ui mb-3">
          Limite de {COLLAB_LIMIT} colaboradores atingido. Remova alguém para convidar outra pessoa.
        </p>
      )}

      {loading ? (
        <div data-testid={COLLAB.loading}><ListSkeleton count={2} /></div>
      ) : error ? (
        <ErrorState message={error} onRetry={load} testid={COLLAB.error} />
      ) : list.length === 0 ? (
        <p data-testid={COLLAB.empty} className="text-[13px] text-brand-ink2 font-ui py-2">
          Ninguém por aqui ainda. Convide alguém pelo e-mail acima.
        </p>
      ) : (
        <ul data-testid={COLLAB.list} className="space-y-2">
          {list.map((c) => {
            const chip = statusChip(c.status);
            const isInvited = (c.status || "").toUpperCase() === "INVITED";
            const when = formatDatePtBr(c.accepted_at || c.invited_at);
            const open = confirm?.id === c.id ? confirm : null;
            return (
              <li
                key={c.id}
                data-testid={`${COLLAB.item}-${c.id}`}
                className="rounded-2xl bg-brand-elevated border border-brand-line p-3"
              >
                <div className="flex items-center gap-3">
                  <span className="min-w-0 flex-1">
                    <span className="block text-[14px] text-brand-ink font-ui truncate">
                      {c.invited_email || c.email || "—"}
                    </span>
                    <span className="flex items-center gap-1.5 mt-0.5">
                      <StatusChip chip={chip} />
                      {when && <span className="text-[11px] text-brand-ink3 font-ui">· {when}</span>}
                    </span>
                  </span>
                  {!open && (
                    <span className="shrink-0 flex items-center gap-1">
                      {isInvited && (
                        <button
                          type="button"
                          data-testid={`${COLLAB.resend}-${c.id}`}
                          onClick={() => setConfirm({ id: c.id, action: "resend", stage: 1 })}
                          aria-label="Reenviar convite"
                          title="Reenviar convite"
                          className="w-9 h-9 flex items-center justify-center rounded-full text-brand-ink2 hover:text-brand-rose hover:bg-brand-surface transition-colors"
                        >
                          <RotateCcw className="w-4 h-4" />
                        </button>
                      )}
                      <button
                        type="button"
                        data-testid={`${COLLAB.revoke}-${c.id}`}
                        onClick={() => setConfirm({ id: c.id, action: "revoke", stage: 1 })}
                        aria-label="Remover acesso"
                        title="Remover acesso"
                        className="w-9 h-9 flex items-center justify-center rounded-full text-brand-ink2 hover:text-brand-rose hover:bg-brand-surface transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </span>
                  )}
                </div>

                {/* Inline confirmations */}
                {open?.action === "resend" && (
                  <InlineConfirm
                    text="Reenviar o convite para este e-mail?"
                    confirmLabel="Reenviar"
                    confirmTestid={`${COLLAB.resendConfirm}-${c.id}`}
                    onConfirm={() => doResend(c.id)}
                    onCancel={() => setConfirm(null)}
                  />
                )}
                {open?.action === "revoke" && open.stage === 1 && (
                  <InlineConfirm
                    text="Remover o acesso desta pessoa?"
                    confirmLabel="Remover"
                    confirmTestid={`${COLLAB.revokeConfirm1}-${c.id}`}
                    onConfirm={() => setConfirm({ id: c.id, action: "revoke", stage: 2 })}
                    onCancel={() => setConfirm(null)}
                  />
                )}
                {open?.action === "revoke" && open.stage === 2 && (
                  <InlineConfirm
                    text="Tem certeza? Isso é definitivo e libera a vaga."
                    confirmLabel="Sim, remover"
                    confirmTestid={`${COLLAB.revokeConfirm2}-${c.id}`}
                    onConfirm={() => doRevoke(c.id)}
                    onCancel={() => setConfirm(null)}
                  />
                )}
              </li>
            );
          })}
        </ul>
      )}
    </Section>
  );
}

function StatusChip({ chip }) {
  const tone = {
    sage: "bg-brand-sage/15 text-brand-sage",
    gold: "bg-brand-gold/15 text-brand-gold",
    muted: "bg-brand-surface text-brand-ink3",
  }[chip.tone] || "bg-brand-surface text-brand-ink3";
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10.5px] tracking-wide font-ui ${tone}`}>
      {chip.label}
    </span>
  );
}

function InlineConfirm({ text, confirmLabel, confirmTestid, onConfirm, onCancel }) {
  return (
    <div className="mt-2.5 flex items-center gap-2 border-t border-brand-line pt-2.5">
      <span className="flex-1 text-[12.5px] text-brand-ink2 font-ui">{text}</span>
      <button
        type="button"
        data-testid={confirmTestid}
        onClick={onConfirm}
        className="shrink-0 min-h-[36px] px-3 rounded-full bg-brand-rose text-white text-[12px] font-ui hover:bg-brand-roseHover transition-colors"
      >
        {confirmLabel}
      </button>
      <button
        type="button"
        onClick={onCancel}
        className="shrink-0 min-h-[36px] px-3 rounded-full bg-brand-surface border border-brand-line text-brand-ink2 text-[12px] font-ui hover:text-brand-rose transition-colors"
      >
        Cancelar
      </button>
    </div>
  );
}
